import { useRef } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function LearningOutcomes() {
  const outcomes = [
    {
      category: 'Knowledge & Understanding',
      icon: 'ri-brain-line',
      color: 'bg-[#E48523]',
      items: [
        'Understand Country as a living cultural system and the protocols that guide respectful practice.',
        'Understand Better Placed, Connecting with Country, and how to embed ICIP protections into project practice.',
      ],
    },
    {
      category: 'Skills & Capabilities',
      icon: 'ri-tools-line',
      color: 'bg-[#BA3D03]',
      items: [
        'Conduct culturally safe engagement with Traditional Custodians and communities.',
        'Determine appropriate levels of community involvement and apply co-design principles.',
      ],
    },
    {
      category: 'Application & Practice',
      icon: 'ri-rocket-line',
      color: 'bg-[#3D1203]',
      items: [
        'Embed Country-centred thinking into planning, design and delivery decisions across the full project lifecycle.',
        'Apply Country-centred thinking to procurement, site practices and delivery considerations.',
      ],
    },
    {
      category: 'Reflection & Growth',
      icon: 'ri-seedling-line',
      color: 'bg-[#E48523]',
      items: [
        'Use reflection and feedback to strengthen practice over time and avoid tokenistic approaches.',
        'Support reporting of outcomes for Country and community (where appropriate).',
      ],
    },
  ];

  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation({ threshold: 0.2 });
  const { ref: gridRef, isVisible: gridVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: certRef, isVisible: certVisible } = useScrollAnimation({ threshold: 0.3 });

  return (
    <section id="learning-outcomes" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-16 transition-all duration-700 ease-out ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-flex items-center px-4 py-2 bg-[#E48523]/10 text-[#BA3D03] rounded-full text-sm font-semibold mb-6">
            What You'll Achieve
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#3D1203] mb-6">
            Overall Learning Outcomes
          </h2>
          <p className="text-lg text-[#3D1203]/70 max-w-3xl mx-auto">
            A concise summary of what your team can apply in NSW projects after completing the course.
          </p>
        </div>

        <div
          ref={gridRef as React.RefObject<HTMLDivElement>}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8"
        >
          {outcomes.map((outcome, index) => (
            <div
              key={index}
              className={`bg-white rounded-xl p-8 shadow-sm hover:shadow-md transition-all duration-500 border border-[#E8C580]/30 ${
                gridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: gridVisible ? `${index * 120}ms` : '0ms' }}
            >
              <div className="flex items-start gap-4 mb-6">
                <div className={`w-14 h-14 ${outcome.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                  <i className={`${outcome.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-2xl font-bold text-[#3D1203] pt-2">{outcome.category}</h3>
              </div>
              <ul className="space-y-4">
                {outcome.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-start gap-3">
                    <i className="ri-arrow-right-circle-fill text-[#E48523] mt-1 flex-shrink-0"></i>
                    <span className="text-[#3D1203]/80 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div
          ref={certRef as React.RefObject<HTMLDivElement>}
          className={`mt-12 bg-gradient-to-br from-[#BA3D03] to-[#3D1203] rounded-2xl p-8 text-white transition-all duration-700 ease-out ${
            certVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-medal-line text-3xl text-white"></i>
            </div>
            <div className="flex-1 text-center lg:text-left">
              <h3 className="text-xl font-bold mb-1">Certificate of Completion</h3>
              <p className="text-white/90 leading-relaxed">
                Receive a completion certificate to support internal reporting and CPD records (where applicable).
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
